var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { GPTLoader } from './gpt-loader';
import { AdEventType } from '../types';
/**
 * GAM-supported banner sizes on `/getjar/games_banner`.
 * Adding a preset here requires the corresponding size to be enabled on the GAM ad unit.
 */
export const BANNER_PRESETS = {
    mobileBanner: [320, 50],
    mobileLargeBanner: [320, 100],
    mobile300x50: [300, 50],
    mobile300x75: [300, 75],
    mobile300x100: [300, 100],
    square: [250, 250],
    rectangle: [300, 250],
    leaderboard: [728, 90],
    billboard: [970, 250],
    wideSkyscraper: [160, 600],
};
/**
 * Deprecated raw size strings → preset name. Used to gracefully map old `config.size` calls.
 * `300x600` (half-page) is not currently provisioned on GAM games_banner.
 */
const LEGACY_SIZE_TO_PRESET = {
    '320x50': 'mobileBanner',
    '300x100': 'mobile300x100',
    '728x90': 'leaderboard',
    '300x250': 'rectangle',
    '160x600': 'wideSkyscraper',
};
/**
 * Resolve a BannerConfig into a concrete preset + size, or null when neither a
 * valid `sizePreset` nor a mappable legacy `size` was provided. Exported so the
 * SDK orchestrator can use the resolved size when building an AY bid request
 * (the same size that GPT will eventually request).
 */
export function resolveBannerSize(config) {
    if (config.sizePreset && config.sizePreset in BANNER_PRESETS) {
        const [w, h] = BANNER_PRESETS[config.sizePreset];
        return { preset: config.sizePreset, w, h };
    }
    if (config.size) {
        const mapped = LEGACY_SIZE_TO_PRESET[config.size];
        if (mapped) {
            console.warn(`[GetJarAds] \`size: "${config.size}"\` is deprecated; use \`sizePreset: "${mapped}"\` instead.`);
            const [w, h] = BANNER_PRESETS[mapped];
            return { preset: mapped, w, h };
        }
        console.warn(`[GetJarAds] Banner size "${config.size}" is not available on GAM games_banner and will be removed. ` +
            `Switch to a supported preset.`);
    }
    return null;
}
export function handleBannerSlotRenderEnded(event, ctx) {
    if (event.slot !== ctx.slot)
        return 'other_slot';
    if (event.isEmpty) {
        // No-fill: hide the reserved container so we don't leave an empty
        // box on the page (the equivalent of GPT's old collapse-empty-divs
        // behaviour, but gated on the authoritative empty signal).
        ctx.hideContainer(ctx.containerId);
        console.warn(`[GetJarAds] No banner ad available for ${ctx.placement}`);
        ctx.eventEmitter.emit(AdEventType.AD_ERROR, {
            placement: ctx.placement,
            adType: 'banner',
            error: 'No ad available',
        });
        return 'empty';
    }
    // Filled: force-resize the rendered iframe + its `__container__`
    // wrapper to the SDK's reserved preset size. GAM/PUC ships a 1×1
    // creative; without this, the slot renders invisibly at 1×1. See the
    // header docstring (#2) for the SafeFrame rationale.
    //
    // `event.size` is intentionally NOT consulted — GAM reports the
    // creative's actual size (1×1) for the PUC, so trusting it would
    // perpetuate the bug. The SDK's preset is the source of truth.
    ctx.resizeFilledContainer(ctx.containerId, ctx.width, ctx.height);
    ctx.eventEmitter.emit(AdEventType.AD_LOADED, {
        placement: ctx.placement,
        adType: 'banner',
        size: ctx.size,
    });
    return 'filled';
}
/**
 * Default `hideContainer` — production wiring. Sets `display: none` on
 * the reserved div if it's still in the DOM. Optional-chain guards
 * against the host page yanking the div between define and renderEnded.
 */
function defaultHideContainer(containerId) {
    if (typeof document === 'undefined')
        return;
    const el = document.getElementById(containerId);
    if (el)
        el.style.display = 'none';
}
/**
 * Default `resizeFilledContainer` — production wiring.
 *
 * GPT's rendered DOM under our reserved container looks like:
 *
 *   <div id="our-container-id" style="min-width/min-height set by us">
 *     <div id="google_ads_iframe_[adUnitPath]_0__container__" style="...1×1...">
 *       <iframe id="google_ads_iframe_[adUnitPath]_0" width="1" height="1" ...>
 *         (cross-origin SafeFrame contents — the PUC)
 *       </iframe>
 *     </div>
 *   </div>
 *
 * We force both the inner `__container__` div and the iframe to the
 * reserved preset width/height (CSS + HTML attrs). Optional-chain guards
 * for partial DOM states (e.g. listener fired before the iframe was
 * inserted — defensive). Also clears any prior `display:none` on the
 * outer container in case `hideContainer` ran for an earlier empty
 * render that's now being re-shown.
 */
function defaultResizeFilledContainer(containerId, w, h) {
    if (typeof document === 'undefined')
        return;
    const outer = document.getElementById(containerId);
    if (!outer)
        return;
    // Make sure the outer container isn't hidden from a prior no-fill.
    outer.style.removeProperty('display');
    const inner = outer.querySelector('div[id$="__container__"]');
    if (inner) {
        inner.style.width = `${w}px`;
        inner.style.height = `${h}px`;
        inner.style.minWidth = `${w}px`;
        inner.style.minHeight = `${h}px`;
    }
    const iframe = (inner !== null && inner !== void 0 ? inner : outer).querySelector('iframe');
    if (iframe) {
        iframe.style.width = `${w}px`;
        iframe.style.height = `${h}px`;
        iframe.style.minWidth = `${w}px`;
        iframe.style.minHeight = `${h}px`;
        iframe.setAttribute('width', String(w));
        iframe.setAttribute('height', String(h));
    }
}
/**
 * GPT Banner Ads Implementation
 * Manages display banner ad slots using Google Publisher Tag
 */
export class GPTBannerAds {
    constructor(eventEmitter, adUnit) {
        this.eventEmitter = eventEmitter;
        this.adUnit = adUnit;
        this.slots = new Map();
    }
    /**
     * Show a banner ad
     */
    show(config, targeting) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // Resolve preset (preferred) or fall back to deprecated raw size
            const resolved = resolveBannerSize(config);
            if (!resolved) {
                throw new Error(`[GetJarAds] Invalid banner config: provide \`sizePreset\` (one of ${Object.keys(BANNER_PRESETS).join(', ')}). ` +
                    `Legacy \`size\` field is supported only for: ${Object.keys(LEGACY_SIZE_TO_PRESET).join(', ')}.`);
            }
            const { w, h } = resolved;
            // Reserve container dimensions to prevent CLS before the slot fills.
            this.reserveContainer(config.containerId, w, h);
            // Reset any prior `display: none` set by a previous no-fill collapse
            // (see `handleBannerSlotRenderEnded`). Idempotent — `removeProperty`
            // is a no-op when nothing was set, so this is safe on a fresh container.
            this.unhideContainer(config.containerId);
            // Ensure GPT is loaded
            yield GPTLoader.load();
            const placement = (_b = (_a = config.placementName) !== null && _a !== void 0 ? _a : config.placement) !== null && _b !== void 0 ? _b : 'unknown';
            // Emit ad requested event
            yield this.eventEmitter.emit(AdEventType.AD_REQUESTED, {
                placement,
                adType: 'banner',
                size: `${w}x${h}`,
            });
            // Check if slot already exists for this container
            const existingSlot = this.slots.get(config.containerId);
            if (existingSlot) {
                console.warn(`[GetJarAds] Banner slot already exists for ${config.containerId}, refreshing`);
                window.googletag.cmd.push(() => {
                    window.googletag.pubads().refresh([existingSlot]);
                });
                return;
            }
            return new Promise((resolve, reject) => {
                window.googletag.cmd.push(() => {
                    try {
                        // Define the ad slot
                        const slot = window.googletag.defineSlot(this.adUnit, [w, h], config.containerId);
                        if (!slot) {
                            reject(new Error('Failed to define banner slot'));
                            return;
                        }
                        for (const [key, value] of Object.entries(targeting)) {
                            slot.setTargeting(key, value);
                        }
                        // Add service
                        slot.addService(window.googletag.pubads());
                        // Listen for slot events. Body lives in `handleBannerSlotRenderEnded`
                        // so the no-fill collapse + filled-resize paths are
                        // unit-testable; see the helper's docstring for the two-layer
                        // fix rationale (collapseDivs:'DISABLED' + force-resize for the
                        // 1×1 PUC creative).
                        const renderEndedListener = window.googletag
                            .pubads()
                            .addEventListener('slotRenderEnded', (event) => {
                            handleBannerSlotRenderEnded(event, {
                                slot,
                                containerId: config.containerId,
                                eventEmitter: this.eventEmitter,
                                placement,
                                width: w,
                                height: h,
                                size: `${w}x${h}`,
                                hideContainer: defaultHideContainer,
                                resizeFilledContainer: defaultResizeFilledContainer,
                            });
                        });
                        const impressionListener = window.googletag
                            .pubads()
                            .addEventListener('impressionViewable', (event) => {
                            if (event.slot === slot) {
                                this.eventEmitter.emit(AdEventType.AD_IMPRESSION, {
                                    placement,
                                    adType: 'banner',
                                    size: `${w}x${h}`,
                                });
                            }
                        });
                        // Display the ad
                        window.googletag.display(config.containerId);
                        // Refresh to load the ad
                        window.googletag.pubads().refresh([slot]);
                        // Store the slot
                        this.slots.set(config.containerId, slot);
                        console.log(`[GetJarAds] Banner ad requested for ${config.containerId}`);
                        resolve();
                    }
                    catch (error) {
                        reject(error);
                    }
                });
            });
        });
    }
    /**
     * Destroy a banner ad slot
     */
    destroy(containerId) {
        const slot = this.slots.get(containerId);
        if (slot) {
            window.googletag.cmd.push(() => {
                window.googletag.destroySlots([slot]);
            });
            this.slots.delete(containerId);
            console.log(`[GetJarAds] Banner slot destroyed: ${containerId}`);
        }
    }
    /**
     * Destroy all banner ad slots
     */
    destroyAll() {
        const slots = Array.from(this.slots.values());
        if (slots.length > 0) {
            window.googletag.cmd.push(() => {
                window.googletag.destroySlots(slots);
            });
            this.slots.clear();
            console.log(`[GetJarAds] All banner slots destroyed`);
        }
    }
    /**
     * Reserve container dimensions before GPT renders to avoid layout shift (CLS).
     * Safe to call repeatedly — only sets minWidth/minHeight if container exists in DOM.
     */
    reserveContainer(containerId, width, height) {
        if (typeof document === 'undefined')
            return;
        const el = document.getElementById(containerId);
        if (!el)
            return;
        el.style.minWidth = `${width}px`;
        el.style.minHeight = `${height}px`;
    }
    /**
     * Undo a previous `display: none` set by `handleBannerSlotRenderEnded`
     * on an `isEmpty` event. Idempotent — `removeProperty` is a no-op on a
     * fresh container, so this is safe regardless of prior state.
     */
    unhideContainer(containerId) {
        var _a;
        if (typeof document === 'undefined')
            return;
        (_a = document.getElementById(containerId)) === null || _a === void 0 ? void 0 : _a.style.removeProperty('display');
    }
}
